import React, { useContext } from "react";
import Img from "gatsby-image";
import { Box, Flex } from "@src/components/Boxes";
import { graphql, Link, useStaticQuery } from "gatsby";
import styled from "@emotion/styled";
import Container from "@src/components/Container";
import Button from "@src/components/Button";
import { Text } from "@src/components/Text";
import { getRelativePath } from "@src/utils/routes";
import { css } from "@emotion/react";
import { FaFacebookF, FaTwitter } from "react-icons/fa";
import Spacer from "@src/components/Spacer";
import { mediaQueries } from "@src/styles/theme";
import Translate from "@src/components/translation/Translate";
import { TranslationContext } from "@src/components/translation/TranslationContext";

const NavItem = styled(Link)<{ last?: boolean }>(
  css`
    display: flex;
    align-items: center;
    padding: 12px 0;
    cursor: pointer !important;
  `,
  (props) =>
    props.last
      ? css``
      : css`
          border-bottom: 1px solid #efefef;
          ${mediaQueries[0]} {
            border-bottom: 0;
          }
        `,
);

const IconImg = styled(Img)`
  margin-right: 25px;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
`;
const TitleIconImg = styled(Img)`
  margin-right: 15px;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;

  ${mediaQueries[0]} {
    margin-right: 25px;
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
  }
`;
const NavText = styled("span")`
  color: #363636;
  display: block;
  font-weight: 400;
  font-size: 18px;

  :hover {
    color: #000;
  }
`;
const BackLink = styled(Link)`
  font-weight: 600;
  margin-bottom: 11px;
  color: #00bcf2;
  :hover {
    color: #00a8d9;
  }
  display: none;
  ${mediaQueries[0]} {
    display: block;
  }
`;
const active = css`
  font-weight: 700 !important;
`;

const BodyContainer = styled("div")`
  width: 100%;
  border: 1px solid #ccc;
  padding: 15px;
  ${mediaQueries[0]} {
    padding: 30px;
  }
  margin-bottom: 30px;
  border-radius: 20px;
  font-size: 18px;
  color: #363636;

  h3 {
    font-size: 12px;
    font-weight: 600;
    color: #c6c6c6;
    text-transform: uppercase;
    line-height: 24px;
    margin-top: 22px;
    margin-bottom: 11px;
  }
  p {
    font-size: 18px;
    color: #363636;
    margin: 0 0 11px;
  }
`;
const BodyTitle = styled("h1")`
  color: #3e425d;
  font-weight: 800;
  line-height: 1em;
  margin: 0;
  font-size: 28px;
  ${mediaQueries[0]} {
    font-size: 34px;
  }
`;
const BodyBannerText = styled("h2")`
  line-height: 1.3em;
  color: #363636;
  font-size: 22px;
  font-weight: 800;
  margin-top: 22px;
  margin-bottom: 11px;
  ${mediaQueries[0]} {
    font-size: 24px;
  }
`;
const ScreenShotBox = styled("div")`
  width: 100%;
  overflow-x: scroll;
  white-space: nowrap;
  margin-top: 0;
  margin-bottom: 11px;
`;

const ScreenShot = styled(Img)`
  border: 1px solid #979797;
  height: 360px;
  display: inline-block;
  margin-right: 20px;
`;
const AppImg = styled(Img)`
  margin-right: 15px;
  display: inline-block;
`;

function ToolkitSubpageLayout({ page, children, screenshots }) {
  const data = useStaticQuery(graphql`
    query {
      directus {
        page_toolkit {
          picker_music_icon {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 29, quality: 90) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
          picker_directions_icon {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 29, quality: 90) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
          picker_timer_icon {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 29, quality: 90) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
          picker_share_icon {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 29, quality: 90) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
          picker_monitor_icon {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 29, quality: 90) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
          picker_generator_icon {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 29, quality: 90) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
          picker_meeting_icon {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 29, quality: 90) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
          picker_selector_icon {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 29, quality: 90) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
        }
      }

      directionsIcon: file(name: { eq: "directions-icon@2x" }, relativePath: { regex: "/toolkit/" }) {
        childImageSharp {
          fluid(maxWidth: 29, quality: 90) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      groupMakerIcon: file(name: { eq: "group-maker-icon@2x" }, relativePath: { regex: "/toolkit/" }) {
        childImageSharp {
          fluid(maxWidth: 29, quality: 90) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      musicIcon: file(name: { eq: "music-icon@2x" }, relativePath: { regex: "/toolkit/" }) {
        childImageSharp {
          fluid(maxWidth: 29, quality: 90) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      noiseMeterIcon: file(name: { eq: "noise-meter-icon@2x" }, relativePath: { regex: "/toolkit/" }) {
        childImageSharp {
          fluid(maxWidth: 29, quality: 90) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      pairShareIcon: file(name: { eq: "pair-share-icon@2x" }, relativePath: { regex: "/toolkit/" }) {
        childImageSharp {
          fluid(maxWidth: 29, quality: 90) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      randomIcon: file(name: { eq: "random-icon@2x" }, relativePath: { regex: "/toolkit/" }) {
        childImageSharp {
          fluid(maxWidth: 29, quality: 90) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      timerIcon: file(name: { eq: "timer-icon@2x" }, relativePath: { regex: "/toolkit/" }) {
        childImageSharp {
          fluid(maxWidth: 29, quality: 90) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      todayIcon: file(name: { eq: "today-icon@2x" }, relativePath: { regex: "/toolkit/" }) {
        childImageSharp {
          fluid(maxWidth: 29, quality: 90) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      apple: file(name: { eq: "app-store-button@2x" }, relativePath: { regex: "/toolkit/" }) {
        childImageSharp {
          fixed(width: 160, quality: 90) {
            ...GatsbyImageSharpFixed_noBase64
          }
        }
      }
      android: file(name: { eq: "play-store-button@2x" }, relativePath: { regex: "/toolkit/" }) {
        childImageSharp {
          fixed(width: 160, quality: 90) {
            ...GatsbyImageSharpFixed_noBase64
          }
        }
      }
      amazon: file(name: { eq: "amazon-button@2x" }, relativePath: { regex: "/toolkit/" }) {
        childImageSharp {
          fixed(width: 160, quality: 90) {
            ...GatsbyImageSharpFixed_noBase64
          }
        }
      }
    }
  `);
  const {
    amazon,
    apple,
    android,
    directus: {
      page_toolkit: {
        picker_music_icon,
        picker_directions_icon,
        picker_timer_icon,
        picker_share_icon,
        picker_monitor_icon,
        picker_generator_icon,
        picker_meeting_icon,
        picker_selector_icon,
      },
    },
  } = data;

  const t = useContext(TranslationContext);

  let titleIcon;
  let titleText;
  let taglineText;
  if (page === "directions") {
    titleIcon = picker_directions_icon;
    titleText = t.translate("directus.page_toolkit_directions.title");
    taglineText = t.translate("directus.page_toolkit_directions.tagline");
  }
  if (page === "groupmaker") {
    titleIcon = picker_generator_icon;
    titleText = t.translate("directus.page_toolkit_groupmaker.title");
    taglineText = t.translate("directus.page_toolkit_groupmaker.tagline");
  }
  if (page === "music") {
    titleIcon = picker_music_icon;
    titleText = t.translate("directus.page_toolkit_music.title");
    taglineText = t.translate("directus.page_toolkit_music.tagline");
  }
  if (page === "noisemeter") {
    titleIcon = picker_monitor_icon;
    titleText = t.translate("directus.page_toolkit_noisemeter.title");
    taglineText = t.translate("directus.page_toolkit_noisemeter.tagline");
  }
  if (page === "thinkpairshare") {
    titleIcon = picker_share_icon;
    titleText = t.translate("directus.page_toolkit_thinkpairshare.title");
    taglineText = t.translate("directus.page_toolkit_thinkpairshare.tagline");
  }
  if (page === "random") {
    titleIcon = picker_selector_icon;
    titleText = t.translate("directus.page_toolkit_random.title");
    taglineText = t.translate("directus.page_toolkit_random.tagline");
  }
  if (page === "timer") {
    titleIcon = picker_timer_icon;
    titleText = t.translate("directus.page_toolkit_timer.title");
    taglineText = t.translate("directus.page_toolkit_timer.tagline");
  }
  if (page === "today") {
    titleIcon = picker_meeting_icon;
    titleText = t.translate("directus.page_toolkit_today.title");
    taglineText = t.translate("directus.page_toolkit_today.tagline");
  }
  return (
    <Container>
      <Box width={["100%", "100%", "100%", 10 / 12]} mx="auto" paddingY="50px">
        <Flex flexDirection={["column-reverse", "row"]}>
          <Flex flexDirection="column" width={["100%", 3 / 12]} pr={["15px"]} pl={["15px", 0]} marginTop={["20px", 0]}>
            <BackLink to={getRelativePath("/toolkit")}>
              {"< "}
              <Translate path="directus.page_toolkit.back_button_text" />
            </BackLink>
            <NavItem to={getRelativePath("/toolkit/directions")}>
              <IconImg fluid={picker_directions_icon.file.childImageSharp.fluid} alt="Directions icon" />
              <NavText css={page === "directions" ? active : null}>Directions</NavText>
            </NavItem>
            <NavItem to={getRelativePath("/toolkit/music")}>
              <IconImg fluid={picker_music_icon.file.childImageSharp.fluid} alt="Music icon" />
              <NavText css={page === "music" ? active : null}>Music</NavText>
            </NavItem>
            <NavItem to={getRelativePath("/toolkit/groupmaker")}>
              <IconImg fluid={picker_generator_icon.file.childImageSharp.fluid} alt="Generator icon" />
              <NavText css={page === "groupmaker" ? active : null}>Group Maker</NavText>
            </NavItem>
            <NavItem to={getRelativePath("/toolkit/noisemeter")}>
              <IconImg fluid={picker_monitor_icon.file.childImageSharp.fluid} alt="Monitor icon" />
              <NavText css={page === "noisemeter" ? active : null}>Noise Meter</NavText>
            </NavItem>
            <NavItem to={getRelativePath("/toolkit/thinkpairshare")}>
              <IconImg fluid={picker_share_icon.file.childImageSharp.fluid} alt="Share icon" />
              <NavText css={page === "thinkpairshare" ? active : null}>Think Pair Share</NavText>
            </NavItem>
            <NavItem to={getRelativePath("/toolkit/random")}>
              <IconImg fluid={picker_selector_icon.file.childImageSharp.fluid} alt="Selector icon" />
              <NavText css={page === "random" ? active : null}>Random</NavText>
            </NavItem>
            <NavItem to={getRelativePath("/toolkit/timer")}>
              <IconImg fluid={picker_timer_icon.file.childImageSharp.fluid} alt="Timer icon" />
              <NavText css={page === "timer" ? active : null}>Timer</NavText>
            </NavItem>
            <NavItem last to={getRelativePath("/toolkit/today")}>
              <IconImg fluid={picker_meeting_icon.file.childImageSharp.fluid} alt="Meeting icon" />
              <NavText css={page === "today" ? active : null}>Today</NavText>
            </NavItem>
          </Flex>
          <Box width={["100%", 9 / 12]} pl={[0, "15px"]}>
            <BodyContainer>
              <Flex alignItems="center">
                <TitleIconImg fluid={titleIcon.file.childImageSharp.fluid} alt={`${titleText} icon`} />
                <BodyTitle>{titleText}</BodyTitle>
              </Flex>
              <BodyBannerText>{taglineText}</BodyBannerText>
              <h3>
                <Translate path="directus.page_toolkit.screenshots_text" />
              </h3>
              <ScreenShotBox>
                {screenshots.map((screenshot, index) => {
                  return (
                    screenshot &&
                    screenshot.file && (
                      <ScreenShot
                        key={`screenshot-${index}`}
                        fixed={screenshot.file.childImageSharp.fixed}
                        alt="Toolkit app screenshot"
                      />
                    )
                  );
                })}
              </ScreenShotBox>
              {children}
              <h3>
                <Translate path="directus.page_toolkit.share_text" />
              </h3>

              <Button
                outline
                marginRight="20px"
                as="a"
                href={
                  "https://twitter.com/intent/tweet?url=https://goo.gl/4ip9Jd&text=Wow!%20Every%20teacher%20tool%20I%20love%20like%20Group%20Maker%20is%20now%20on%20@ClassDojo%20-%20Check%20out%20Toolkit:&hashtags=OMGToolkit"
                }
                target="_blank"
                rel="noopener noreferrer"
                data-dnt="true"
              >
                <Flex alignItems="center">
                  <FaTwitter />
                  <Spacer marginRight="5px" />
                  <Translate path="directus.page_toolkit.share_twitter" />
                </Flex>
              </Button>
              <Button
                outline
                as="a"
                href={
                  "https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fwww.classdojo.com%2Ftoolkit%2F%3Futm_source%3Dfacebook%26utm_medium%3Dsocial%26utm_campaign%3D2017-07-toolkit"
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                <Flex alignItems="center">
                  <FaFacebookF />
                  <Spacer marginRight="5px" />
                  <Translate path="directus.page_toolkit.share_facebook" />
                </Flex>
              </Button>
            </BodyContainer>
            <Box>
              <Text textAlign="center" fontSize={["18px", "16px"]} fontWeight={[600, 400]}>
                <Translate path="directus.page_toolkit.apps_text" />
              </Text>
              <Flex flexDirection={["column", "row"]} alignItems="center" width="100%">
                <a
                  href="https://itunes.apple.com/us/app/classdojo/id552602056?mt=8"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AppImg fixed={apple.childImageSharp.fixed} alt="Apple Store banner" />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.classdojo.android"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AppImg fixed={android.childImageSharp.fixed} alt="Android Store banner" />
                </a>
                <a href="https://www.amazon.com/ClassDojo/dp/B01AIM0EGO" target="_blank" rel="noopener noreferrer">
                  <AppImg fixed={amazon.childImageSharp.fixed} alt="Amazon Store banner" />
                </a>
              </Flex>
            </Box>
          </Box>
        </Flex>
      </Box>
    </Container>
  );
}

export default ToolkitSubpageLayout;
